import type { TeamRolesView } from '../../../pages/new-designs/utils/teamRoles';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { API } from '../../../api';

export interface IUserData {
  name: any;
  email: string;
  country: string;
  firstName: string;
  lastName: string;
  timezone: string;
  headshot: string;
  dob: string;
  ftue: boolean;
  phoneNumber: string;
  phonePrefix: string;
  gender: string;
  ethnicity: string;
  firstGenStudent: string;
  bio: string;
  isActive: boolean;
  communicationId: string;
  social: {
    facebook: string;
    instagram: string;
    youtube: string;
    twitter: string;
    linkedIn: string;
  };
  // social: string[];
  disability: string[];
  category: string;
  hobbies: string[];
  // photo?: File;
}

interface IData {
  personal_details: IUserData;
  gettingStarted: boolean;
  admins: string[];
  mentees: string[];
  mentors: string[];
  connection: string;
  lastseen: undefined | { id: string; name: string; role: string; active: boolean };
  settings: {
    gender: boolean;
    country: boolean;
    firstGenerationStudent: boolean;
    ethnicity: boolean;
    dob: boolean;
    disabilityType: boolean;
    category: boolean;
    timezone: boolean;
    workHistory: boolean;
    educationHistory: boolean;
    hobbiesInterests: boolean;
    company: boolean;
    headshot: boolean;
    bio: boolean;
    phoneNumber: boolean;
  };
  role: Number;
  bio: Object;
  videoIntro: string;
  upVote: number;
  matchCount: number;
  aiMentor: boolean;
  topics: any;
  selectedTopics: any;
  industries: any;
  selectedIndustries: any;
  educationHistory: any;
  workHistory: any;
  objectivesNumber: number;
  learningObjectives: {
    description: string;
    id: string;
    mentors: [];
    notes: [];
    objective: string;
    status: string;
  }[];
  _id: string;
  mentorshipScreen: boolean;
  orgDepartments?: { _id: string; name: string }[];
  departments?: string[];
  memberAttributes?: string[];
  orgMemberAttributes?: Array<{
    _id: string;
    name: string;
    values: Array<{ _id: string; value: string }>;
  }>;
  groups?: string[];
  groupsAll?: string[];
  /**
   * Org-level roles carried by the invitation ('admin' | 'mentor' | 'mentee'),
   * persisted by the BE at signup. Group membership is per program, so this is
   * the only signal for an org-only invitee (zero memberships).
   */
  orgRoles?: string[];
  memberAttributesChanged?: boolean;
  /** "Role in <team>": the set-up roles of the org's teams and the member's answers (GET /getUserProfile). */
  teamRoles?: TeamRolesView | null;
  /**
   * Org catalog value ids this member had in front of them the last time they
   * answered the attributes prompt (BE: memberAttributesAckIds). Absent for
   * anyone who has not answered since the field shipped. Used by
   * useMemberAttributesPrompt to ask again only once the catalog grows.
   */
  memberAttributesAckIds?: string[];
  /**
   * Org attribute LIST ids this member answered with "None of these apply to
   * me" (BE: memberAttributesDeclinedLists). Absent/empty for anyone who has
   * not answered since the field shipped, which is how useMemberAttributesPrompt
   * tells a deliberately empty list from one that was never answered.
   */
  memberAttributesDeclinedLists?: string[];
  /** Charlie / Copilot onboarding (BE: stages 1–6, status true when finished) */
  charlie?: {
    stage?: number;
    status?: boolean;
    convId?: string;
  };
}

interface IUser {
  loading: boolean;
  error: boolean;
  errorText: string;
  data: IData | undefined;
  bioTextCount: number;
  /** When `data` was last fetched (ms). redux-persist restores an older one. */
  fetchedAt?: number;
}

/**
 * The profile is persisted across reloads, so the first render of a page can
 * carry one from an earlier visit. Prompts that decide from it (the attributes
 * gate, the "Welcome back" checklist) wait for one fetched since this load.
 */
const PAGE_LOADED_AT = Date.now();
export const isProfileFresh = (state: IUser) => !!state.data && (state.fetchedAt ?? 0) >= PAGE_LOADED_AT;

const initialState: IUser = {
  loading: false,
  error: false,
  errorText: '',
  data: undefined,
  bioTextCount: 0
};

export const fetchUserProfile = createAsyncThunk('userprofile', async () => {
  const res = await API.getUserProfile();
  return { ...res, fetchedAt: Date.now() };
});

const userProfileSlice = createSlice({
  name: 'user profile',
  initialState,
  reducers: {
    update: (state, action) => {
      state.data = action.payload;
    },
    userHobbiesUpdate: (state, action) => {
      if (state.data) {
        state.data.personal_details.hobbies = action.payload;
      }
    },
    userMentorshipScreen: (state, action) => {
      if (state.data) {
        state.data.mentorshipScreen = true;
      }
    },
    userVideoUpdate: (state, action) => {
      if (state.data) {
        state.data.videoIntro = action.payload;
      }
    },
    userDisabilityUpdate: (state, action) => {
      if (state.data) {
        state.data.personal_details.disability = action.payload;
      }
    },
    userSocialUpdate: (state, action) => {
      if (state.data) {
        state.data.personal_details.social = action.payload;
      }
    },
    userFtueUpdate: (state, action) => {
      if (state.data) {
        state.data.personal_details.ftue = false;
      }
    },
    userBioUpdate: (state, action) => {
      if (state.data) {
        state.data.personal_details.bio = action.payload;
      }
    },
    userBioCountUpdate: (state, action) => {
      state.bioTextCount = action.payload;
    },
    userTopicsUpdate: (state, action) => {
      if (state.data) {
        state.data.selectedTopics = action.payload;
      }
    },
    userIndustriesUpdate: (state, action) => {
      if (state.data) {
        state.data.selectedIndustries = action.payload;
      }
    },
    userGettingStartedUpdate: (state, action) => {
      if (state.data) {
        state.data.gettingStarted = action.payload;
      }
    },
    updateObjectives: (state, action) => {
      if (state.data) {
        state.data.learningObjectives = action.payload;
      }
    },
    userMemberAttributesUpdate: (state, action) => {
      if (state.data) {
        state.data.memberAttributes = action.payload;
      }
    },
    userMemberAttributesChangedUpdate: (state, action) => {
      if (state.data) {
        state.data.memberAttributesChanged = action.payload;
      }
    },
    /**
     * Mirror the acknowledgement the BE records on every attributes save, so
     * the prompt does not re-open on a stale ack before the next profile fetch.
     */
    userMemberAttributesAckUpdate: (state, action: PayloadAction<string[]>) => {
      if (state.data) {
        state.data.memberAttributesAckIds = action.payload;
      }
    },
    /**
     * Mirror the lists the member just declined, for the same reason as the ack
     * above: the prompt's requirement is derived from the profile, so without
     * this a save would leave every declined list reading as unanswered until
     * the next profile fetch and the prompt would re-open on the spot.
     */
    userMemberAttributesDeclinedUpdate: (state, action: PayloadAction<string[]>) => {
      if (state.data) {
        state.data.memberAttributesDeclinedLists = action.payload;
      }
    },
    /** The member's "Role in <team>" answers, as saved (utils/teamRoles.ts viewAfterAnswers). */
    userTeamRolesUpdate: (state, action: PayloadAction<TeamRolesView | null>) => {
      if (state.data) {
        state.data.teamRoles = action.payload;
      }
    },
    /** Sync org catalog after PUT /groups/:grpId/memberAttributes (modals refetch). */
    setOrgMemberAttributes: (state, action: PayloadAction<IData['orgMemberAttributes']>) => {
      if (state.data) {
        state.data.orgMemberAttributes = action.payload ?? [];
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = false;
        state.errorText = '';
        // Keep the last-known profile in place while a refetch is in flight.
        // getToken() re-dispatches this on every page load, and the /app/*
        // route tree is conditionally mounted on `userProfile.data`. Clearing
        // it here tore that tree down mid-refresh, so a deep-linked /app/* URL
        // fell through to the catch-all -> /login and bounced the user to
        // their default dashboard. `loading` still flips, so any component
        // that wants a spinner can key off that instead.
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.error = false;
        state.errorText = '';
        state.data = action.payload.data;
        state.fetchedAt = action.payload.fetchedAt;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
        state.errorText = action.error.message || 'Something Went Wrong';
        state.data = undefined;
      });
  }
});

export default userProfileSlice.reducer;

export const userInfoUpdate = userProfileSlice.actions.update;

export const {
  userHobbiesUpdate,
  userMemberAttributesUpdate,
  userMemberAttributesChangedUpdate,
  userMemberAttributesAckUpdate,
  userMemberAttributesDeclinedUpdate,
  userTeamRolesUpdate,
  setOrgMemberAttributes
} = userProfileSlice.actions;

export const { userDisabilityUpdate } = userProfileSlice.actions;

export const { userSocialUpdate } = userProfileSlice.actions;
export const { userBioUpdate, userBioCountUpdate } = userProfileSlice.actions;

export const userActions = userProfileSlice.actions;
